import clustererMarkers from "./helpers/clustererMarkers";
import MapMarker from "./components/MapMarker/MapMarker";
import styles from "./TextMaps.module.scss";
import TextMapsBlockProps from "./TextMapsProps.interface";

import { GoogleMap, MarkerClusterer, useJsApiLoader } from "@react-google-maps/api";
import { useSelector } from "react-redux";
import Button from "components/Button/Button";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import Paragraph from "components/Paragraph/Paragraph";
import React, { FunctionComponent, useRef, useCallback, Fragment } from "react";
import useAnchor from "blocks/hooks/useAnchor";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import isBrowserContext from "helpers/isBrowserContext";

const TextMaps: FunctionComponent<TextMapsBlockProps> = (props) => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);
    const settings = useSelector(state => state.settings.content);

    const identifier = useAnchor(props.settings.anchorMenuLabel);
    const { splitClasses, splitStyles } = useSplitBackground(props);

    // Text Settings
    const headlineCaseClass = props.settings.headlineCase ? styles[`case_${props.settings.headlineCase}`] : undefined;

    // Map Settings
    const mapSize = props.settings.size ? styles[props.settings.size] : undefined;
    const mapStyles = settings?.theme?.googleMapStyles && JSON.parse(settings?.theme?.googleMapStyles);
    const clustererMarker = clustererMarkers(settings?.theme);
    const markers = props.content.markers;
    const { isLoaded } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: "AIzaSyARZOcVyLEp-RkXlXed91gvaF3po4ArMu4"
    }); const mapRef = useRef(null);

    const onLoad = useCallback(function callback(map) {
        if (isBrowserContext()) {
            const bounds = new window.google.maps.LatLngBounds();

            markers.map(item => {
                const position = { lat: Number(item.content.latitude), lng: Number(item.content.longitude) };
                bounds.extend(position);
            });

            map.fitBounds(bounds);
        }
    }, []);

    const mapsOptions = {
        styles: mapStyles,
        disableDefaultUI: true,
        zoomControl: true,
    };

    const containerStyle = {
        height: props.settings.height ? `${props.settings.height}px` : "600px"
    };

    const center = {
        lat: props.settings.latitude ? props.settings.latitude : 0,
        lng: props.settings.longitude ? props.settings.longitude : 0
    };

    const zoomLevel = props.settings.zoomLevel ? props.settings.zoomLevel : 12;
    const mapPositionClass = props.settings.mapPosition === "Map Right" ? ` ${styles.has_map_right}` : undefined;

    return (
        <section className={combineClasses(styles.wrapper, ...wrapperClasses, mapSize)} style={{ ...wrapperStyles, ...splitStyles }} id={identifier}>
            <div className={combineClasses(styles.container, ...splitClasses, mapPositionClass, mapSize)}>
                {/* Text */}
                <div className={styles.content}>
                    {/* Tagline */}
                    {props.content.tagline ? (
                        <span className={styles.tagline}>{props.content.tagline}</span>
                    ) : null}

                    {/* Headline */}
                    {props.content.headline ? (
                        <DynamicHeading className={combineClasses(styles.headline, headlineCaseClass)} text={props.content.headline} headlineSize={props.settings.headlineSize} headlineWeight={props.settings.headlineWeight} />
                    ) : null}

                    {/* Text */}
                    {props.content.bodyText ? (
                        <Paragraph className={styles.text} innerHTML={props.content.bodyText} />
                    ) : null}

                    {/* Buttons */}
                    {props.content.buttons && props.content.buttons.length > 0 ? (
                        <div className={styles.buttons}>
                            {props.content.buttons.map((button, index) => {
                                return (
                                    <Button key={index} to={button.content.link} className={styles.button} variant={button.content.variant} {...button.content}>{button.content.label}</Button>
                                );
                            })}
                        </div>
                    ) : null}
                </div>

                {/* Google Maps */}
                <div className={styles.map_wrapper}>
                    {isLoaded === true ? (
                        <GoogleMap ref={mapRef} onLoad={onLoad} center={center} zoom={zoomLevel} options={mapsOptions} mapContainerStyle={containerStyle}>
                            {markers && markers.length > 0 ? (
                                <MarkerClusterer styles={clustererMarker} maxZoom={12} options={{ gridSize: 30, minimumClusterSize: 1 }}>
                                    {(clusterer) =>
                                        <Fragment>
                                            {markers.map((marker, index) => {
                                                const markerWidth = marker?.content?.customMarker?.umbracoWidth ? marker.content.customMarker.umbracoWidth : settings?.theme?.assetMapsPin?.umbracoWidth ? settings?.theme?.assetMapsPin.umbracoWidth : 60;
                                                const markerHeight = marker?.content?.customMarker?.umbracoHeight ? marker.content.customMarker.umbracoHeight : settings?.theme?.assetMapsPin?.umbracoHeight ? settings?.theme?.assetMapsPin.umbracoHeight : 60;

                                                return (
                                                    <MapMarker key={marker.contentUdi} index={index} marker={marker} markerWidth={markerWidth} markerHeight={markerHeight} showClusters={props.settings.clusters} clusterer={clusterer} markerImageFallback={settings?.theme?.assetMapsPin?._url ? settings.theme.assetMapsPin._url : ""} />
                                                );
                                            })}
                                        </Fragment>
                                    }
                                </MarkerClusterer>
                            ) : null}
                        </GoogleMap>
                    ) : null}
                </div>
            </div>
        </section>
    );
};

export default TextMaps;
